import { FormikProvider, useFormik } from 'formik';
import { useCallback, useMemo } from 'react';
import { useAlert } from '../../../AlertProvider';

import { Box } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { CancelSessionOutput } from '@uplift/api';
import { useCurrentuser, useTimezone } from '@uplift/contexts';
import { useL10n, useTimeoutResettableState } from '@uplift/hooks';
import { CalendarDateService } from '@uplift/services';
import { ProviderMedicalRole, UserRole, UserStatus } from '@uplift/types';
import { DisplayName } from '@uplift/utils';

import { FloatingAlert } from '../../../Alert';
import { Button } from '../../../Button';
import { CheckboxWithLabel, RadioControl, TextInput } from '../../../Inputs';
import { Loader } from '../../../Loader';
import {
  ModalFullPage, ModalFullPageActions, ModalFullPageContent, ModalFullPageTitle, ModalFullPageType
} from '../../../ModalFullPage';
import { Tooltip } from '../../../Tooltip';
import { CancelSessionReasonAutocomplete } from './cancel-session-reason-autocomplete.component';
import { useCancelModalStyles } from './styles';
import { CancelFormModalCommonProps } from './types';
import {
  useCancelSessionForm, UseCancelSessionFormArgs, useCancelSessionFormValidationSchema
} from './use-cancel-session-form.hook';
import { useAppointmentCancelAttributesQuery } from '@uplift/queries';

export type ProviderCancelSessionModalProps =
  CancelFormModalCommonProps
  & Pick<ModalFullPageType, 'onClose'>
  & {
    open: boolean;
    patientDisplayName: DisplayName;
    patientId: string;
    patientStatus: UserStatus;
    showChargeLateFee?: boolean;
    modalTitleOverride?: string;
    onSubmit?: (response: CancelSessionOutput) => Promise<void>;
  };

export const ProviderCancelSessionModal: React.VFC<ProviderCancelSessionModalProps> = ({
  open,
  appointmentId,
  fromDatetime,
  patientDisplayName,
  parentId,
  cancelMode = 'manual',
  modalTitleOverride,
  onClose,
  ...props
}) => {
  const l10n = useL10n();
  const currentUser = useCurrentuser();
  const validationSchema = useCancelSessionFormValidationSchema(l10n);
  const alert = useAlert();
  const { classes } = useCancelModalStyles();
  const timezone = useTimezone();
  const isPsychiatrist = currentUser.medicalRole === ProviderMedicalRole.psychiatrist;
  const [ showAlert, setShowAlert ] = useTimeoutResettableState(5000, false);
  const { data: cancelAttributes, isInitialLoading: isCancelAttributesLoading } = useAppointmentCancelAttributesQuery({
    appointmentId: appointmentId,
    fromDatetime: fromDatetime,
    seriesId: parentId,
  }, { enabled: !!appointmentId });
  const appointmentDate = useMemo(() => CalendarDateService.date(fromDatetime), [ fromDatetime ]);

  const onSuccess: UseCancelSessionFormArgs['onSuccess'] = useCallback(async (data, formValues) => {
    props?.onSubmit?.(data);

    const alertMessage = {
      all: l10n.sessions.successfullyCanceledAll,
      single: l10n.sessions.successfullyCanceled,
    }[formValues.recurring];

    alert.success(alertMessage);

    onClose?.();
  }, [ alert, l10n.sessions, onClose, props ]);

  const onError: UseCancelSessionFormArgs['onError'] = useCallback(() => {
    alert.error(l10n.sessions.cancelError);

    onClose?.();
  }, [ alert, l10n.sessions.cancelError, onClose ]);

  const { onSubmit, initialValues } = useCancelSessionForm({
    cancelMode,
    appointmentId,
    fromDatetime,
    parentId,
    onSuccess,
    onError
  });

  const formik = useFormik({
    initialValues,
    onSubmit,
    validationSchema,
    isInitialValid: false,
    enableReinitialize: true,
  });

  const handleDisabledClick = useCallback(() => {
    setShowAlert(true);
  }, [ setShowAlert ]);

  const handleClose = useCallback(() => {
    formik.resetForm();

    onClose?.();
  }, [ formik, onClose ]);

  const recurringInput = useMemo(() => {
    if (!parentId || isPsychiatrist) return null;

    const options = [
      {
        value: 'single',
        label: `${appointmentDate.tz(timezone).format('ddd, MMM D')} at ${appointmentDate.tz(timezone).format('h:mm a')}`,
      },
      {
        value: 'all',
        label: l10n.schedule.cancelAppointment.allSessions,
      }
    ];

    return (
      <RadioControl
        label={l10n.schedule.cancelAppointment.whatToCancel}
        name="recurring"
        values={options}
      />
    );
  }, [ parentId, isPsychiatrist, appointmentDate, timezone, l10n.schedule.cancelAppointment ]);

  const lateFeeText = useMemo(() => {
    if (cancelAttributes?.disabledLateFeeReason === 'medicaid') {
      return l10n.schedule.cancelAppointment.feeText.medicaid;
    }

    if (
      cancelAttributes?.disabledLateFeeReason === 'initial_user'
      || cancelAttributes?.disabledLateFeeReason === 'no_payment_method'
    ) {
      return l10n.schedule.cancelAppointment.feeText.isInitial;
    }

    const now = CalendarDateService.date();

    // session was 7 days ago
    if (now.diff(appointmentDate, 'days') > 7) {
      return l10n.schedule.cancelAppointment.feeText.over7DaysOld;
    }

    // session is more than 24 hours in the future
    if (appointmentDate.diff(now, 'hours') > 24) {
      return l10n.schedule.cancelAppointment.feeText.over24hrs;
    }

    // 24 hours before to 7 days after
    return l10n.schedule.cancelAppointment.feeText.within24hrs;
  }, [ appointmentDate, l10n.schedule, cancelAttributes ]);

  const chargeFeeInput = useMemo(() => {
    if (!cancelAttributes) {
      return null;
    }

    const disableLateFee = !!cancelAttributes.disabledLateFeeReason;

    const label = (
      <Box display="flex" alignItems="center" gap=".5rem">
        <Box>
          {l10n.schedule.cancelAppointment.fee}
        </Box>
        <Tooltip title={lateFeeText}>
          <InfoOutlined />
        </Tooltip>
      </Box>
    );

    if (cancelAttributes.disabledLateFeeReason === 'session_is_sponsored') {
      return null;
    }

    return (
      <Box onClick={disableLateFee ? handleDisabledClick : undefined}>
        <CheckboxWithLabel
          label={label}
          name="chargeFee"
          color="secondary"
          disabled={disableLateFee}
        />
      </Box>
    );
  }, [ handleDisabledClick, l10n.schedule.cancelAppointment.fee, lateFeeText, cancelAttributes ]);

  const modalTitle = useMemo(() => {
    if (modalTitleOverride) {
      return modalTitleOverride;
    }

    if (formik.values.recurring === 'all') {
      return l10n.schedule.cancelAppointment.allWith(patientDisplayName?.first);
    }

    return l10n.schedule.cancelAppointment.sessionWith(patientDisplayName?.first);
  }, [ modalTitleOverride, formik.values.recurring, l10n.schedule.cancelAppointment, patientDisplayName?.first ]);

  return (
    <ModalFullPage
      open={open}
      transition
      mobileOnly="sm"
      closeIcon
      onClose={handleClose}
    >
      {({ displayMobileOnlyStyles }) => (
        <FormikProvider value={formik}>
          <ModalFullPageTitle displayMobileOnlyStyles={!!displayMobileOnlyStyles}>
            {modalTitle}
          </ModalFullPageTitle>
          <ModalFullPageContent className={classes.content}>
            {isCancelAttributesLoading && (
              <Loader />
            )}
            {!isCancelAttributesLoading && (
              <Box display="flex" flexDirection="column" gap="1rem" px={1}>
                <FloatingAlert severity="info" open={showAlert}>
                  {lateFeeText}
                </FloatingAlert>
                {recurringInput}
                <Box maxWidth={displayMobileOnlyStyles ? 'initial' : '47ch'}>
                  <CancelSessionReasonAutocomplete
                    label={l10n.schedule.cancelAppointment.whyAreYouCanceling}
                    name="cancelationReason"
                    userRole={currentUser.role as keyof typeof UserRole}
                    fromDatetime={fromDatetime}
                  />
                </Box>
                <TextInput
                  name="cancelNote"
                  label={l10n.schedule.cancelAppointment.addNote}
                  multiline
                  fullWidth
                  rows={2}
                  color="secondary"
                />
                {chargeFeeInput}
              </Box>
            )}
          </ModalFullPageContent>
          {!isCancelAttributesLoading && (
            <ModalFullPageActions displayMobileOnlyStyles={displayMobileOnlyStyles}>
              <Button variant="text" onClick={handleClose}>
                {formik.values.recurring === 'all'
                  ? l10n.schedule.cancelAppointment.keepAllSessions
                  : l10n.schedule.cancelAppointment.keepSession}
              </Button>
              <Button
                variant="contained"
                color="error"
                loading={formik.isSubmitting}
                onClick={formik.handleSubmit}
                disabled={!formik.isValid}
              >
                {formik.values.recurring === 'all'
                  ? l10n.schedule.cancelAppointment.cancelAllSessions
                  : l10n.schedule.cancelAppointment.cancelSession
                }
              </Button>
            </ModalFullPageActions>
          )}
        </FormikProvider>
      )}
    </ModalFullPage>
  );
};
